import React from "react";
import {toast, ToastOptions } from "react-hot-toast";
import { CloseButton } from "@design/ui/close-button";
import { UserFacingError, toError } from "./errors";
import { diagnoseDocumentError } from "./document";

export const toastSuccess = toast.success;
export const toastError = (message: unknown) => {
    const error = typeof message === "string"
        ? new UserFacingError(message, new Error(message))
        : toError(message);

    const diagnosedError = diagnoseDocumentError(error);

    if (diagnosedError instanceof UserFacingError) {
        toast.error(diagnosedError.userMessage);
    } else {
        console.error(diagnosedError);
    }
};
export const toastDedupedError = (message: string, toastId: string) => toast.error(message, { id: toastId });
export const toastInfo = toast;
export const toastWarning = (message, options?: ToastOptions) => toast(message, {
    icon: "⚠️",
    ...options
});
export const toastLoading = (message: string, options?: ToastOptions) => toast.loading(message, {
    duration: Infinity,
    ...options
});
export const toastDismiss = (toastId?: string) => toast.dismiss(toastId);

export const canOpenBrowserWindow = (): boolean => {
    try {
        return Office.context.requirements.isSetSupported("OpenBrowserWindowApi", "1.1");
    } catch {
        return false;
    }
};

export const remediationLink = (remediationUrl: string, onOpen?: () => void) =>
    React.createElement(
        "a",
        {
            href: remediationUrl,
            target: "_blank",
            rel: "noopener noreferrer",
            onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
                onOpen?.();
                if (canOpenBrowserWindow()) {
                    event.preventDefault();
                    Office.context.ui.openBrowserWindow(remediationUrl);
                }
            },
            style: {
                alignSelf: "flex-start",
                cursor: "pointer",
                color: "inherit",
                textDecoration: "underline",
                fontSize: "0.85rem",
            },
        },
        "Open in Icertis"
    );

const dismissibleRow = (message: string, toastId: string) =>
    React.createElement(
        "div",
        { style: { display: "flex", alignItems: "flex-start", gap: "8px" } },
        React.createElement("span", { style: { flex: 1 } }, message),
        React.createElement(CloseButton, {
            onClick: () => toast.dismiss(toastId),
            "aria-label": "Dismiss",
            size: "icon-xs",
            className: "shrink-0",
        })
    );

export const toastDismissibleError = (message: string, options?: ToastOptions, remediationUrl?: string, onRemediationOpen?: () => void) =>
    toast.error(
        (t) => remediationUrl
            ? React.createElement(
                "div",
                { style: { display: "flex", flexDirection: "column", gap: "8px" } },
                dismissibleRow(message, t.id),
                remediationLink(remediationUrl, onRemediationOpen)
            )
            : dismissibleRow(message, t.id),
        options
    );

export const toastActionableError = (
    message: string,
    action: { label: string; onClick: () => void },
    options?: ToastOptions,
    remediationUrl?: string,
    onRemediationOpen?: () => void
) =>
    toast.error(
        (t) => React.createElement(
            "div",
            { style: { display: "flex", flexDirection: "column", gap: "8px" } },
            dismissibleRow(message, t.id),
            React.createElement(
                "button",
                {
                    type: "button",
                    onClick: () => {
                        toast.dismiss(t.id);
                        action.onClick();
                    },
                    style: {
                        alignSelf: "flex-start",
                        background: "none",
                        border: "1px solid currentColor",
                        borderRadius: "4px",
                        cursor: "pointer",
                        padding: "2px 8px",
                        fontSize: "0.85rem",
                        color: "inherit",
                    },
                },
                action.label
            ),
            remediationUrl ? remediationLink(remediationUrl, onRemediationOpen) : null
        ),
        options
    );
